/* eslint-disable */
import L from 'leaflet';
import './pdf-sheet-plugin.scss';
import 'leaflet-path-drag';
import Vue from 'vue';
// import Vuetify from 'vuetify';
import PdfSheetComponent from './pdf-sheet-component';
import { PdfSheetInput } from './pdf-sheet-input';
import { PdfSheetSizeInput } from './pdf-sheet-size-input';

const OPTIONS = {
  useVue: true,
  size: 'a0',
  scale: 500, // meters in one cm

  orientation: 'album',
  locale: {
    album: 'Album',
    landscape: 'Landscape',
    m: 'm',
    km: 'km',
    mm: 'mm',
    Scale: 'Scale',
    Size: 'Size',
    Orientation: 'Orientation',
    scale_unit: 'm in 1 cm',
  },
  sizeLimit: [3000, 3000],
  // exampleScales: [100, 200, 500, 1000],
  exampleScales: [
    { name: '10K', size: 100 },
    { name: '50K', size: 500 },
    { name: '100K', size: 1000 },
    { name: '200K', size: 2000 },
    { name: '1M', size: 10000 },
  ],
  orientations: [
    { name: 'album', ico: '<i class="v-icon mdi mdi-crop-portrait"></i>' },
    { name: 'landscape', ico: '<i class="v-icon mdi mdi-crop-landscape"></i>' },
  ],
  pageFormats: [
    // Size in mm of various paper formats
    { name: 'max', size: [3000, 3000] },
    { name: 'a0', size: [841, 1189] },
    { name: 'a1', size: [594, 841] },
    { name: 'a2', size: [420, 594] },
    { name: 'a3', size: [297, 420] },
    { name: 'a4', size: [210, 297] },
    // { name: 'a5', size: [148, 210] },
  ],
  rectangleStyle: {
    color: '#FFA500',
    draggable: true,
  },
};

class PdfSheetPlugin extends L.Control {
  initialize(options) {
    this.options = { ...OPTIONS, ...options };

    this._rec;
    this._center = null;
    /** @type HTMLElement */
    this._content = null;
    this._orientations = null;

    this._geomToFit = null;

    const size = this._getSize(this.options.size);
    this.options.sizeX = size[0];
    this.options.sizeY = size[1];
  }

  onAdd(map) {
    this.map = map;
    const container = L.DomUtil.create('div', 'leaflet-control leaflet-bar pdf-sheet-plugin');
    container.appendChild(this._getContent());
    L.DomEvent.disableClickPropagation(container);
    this._drawRectangle();
    return container;
  }

  onRemove() {
    this.map = null;
    this._cleanHtml();
    this._cleanRectangle();
  }

  addGeomToFit(geom) {
    if (geom && geom instanceof L.Layer) {
      this._geomToFit = geom;
      const bounds = geom.getBounds();
      this._center = bounds.getCenter();

      const sw = L.Projection.SphericalMercator.project(bounds.getSouthWest());
      const ne = L.Projection.SphericalMercator.project(bounds.getNorthEast());
      const dx = ne.x - sw.x;
      const dy = ne.y - sw.y;
      const size = this._getSize(this.options.size);
      let scale;
      if (dx > dy) {
        this.options.orientation = 'landscape';
        scale = (dx / size[1]) * 10;
      } else {
        this.options.orientation = 'album';
        scale = (dy / size[0]) * 10;
      }
      this.options.scale = Math.round(scale / 100) * 100;
    }
  }

  calculateRectangleEdges() {
    const edgeSize = this.options.size;
    const { scale } = this.options;
    this._center = this._center || this.map.getCenter();
    const edges = this._calculateRectangleEdges({ edgeSize, scale, center: this._center });
    return edges.map((x) => L.Projection.SphericalMercator.unproject(x));
  }

  _getSize(s) {
    let size;
    if (typeof s === 'string') {
      const sizeObj = this.options.pageFormats.find((x) => x.name === s);
      size = sizeObj && sizeObj.size;
    } else {
      size = s;
    }
    return size;
  }

  _cleanHtml() {
    L.DomUtil.remove(this._content);
  }

  _cleanRectangle() {
    this._rec.remove();
    this._rec.off();
    this._rec = null;
  }

  _getContent() {
    this._content = L.DomUtil.create('div');

    if (this.options.useVue) {
      const component = new Vue({
        template: '<pdf-sheet-component :options="options"/>',
        data: () => ({
          options: this.options,
        }),
        components: {
          PdfSheetComponent,
        },
      });

      // timeout to mount content after plugin added
      setTimeout(() => {
        component.$mount(this._content);
        component.$on('change', (opt) => {
          this.options = Object.assign(this.options, opt);
          this._redrawRectangle();
        });
      }, 0);
    } else {
      this._orientations = this._createOrientationSelector();
      this._size = this._createSizeSelector();
      this._scale = this._createScaleSelector();

      [this._orientations, this._size, this._scale].forEach((x) => {
        this._content.appendChild(x.getContainer());
        x.options.onRedraw = (value) => {
          this.options[x.name] = value;
          this._redrawRectangle();
        };
      });
    }

    L.DomEvent.disableClickPropagation(this._content);

    return this._content;
  }

  _createOrientationSelector() {
    const self = this;
    return new PdfSheetInput({
      name: 'orientation',
      value: self.options.orientation,
      label: self.options.locale.Orientation,
      input: false,
      selectArray: this.options.orientations,
      getHtml(item) {
        return item.ico;
      },
    });
  }

  _createScaleSelector() {
    const self = this;
    return new PdfSheetInput({
      name: 'scale',
      value: self.options.scale,
      label: self.options.locale.Scale,
      unit: self.options.locale.scale_unit,
      selectArray: this.options.exampleScales.map((x) => ({ name: x })),
      getHtml(item) {
        return item.name >= 1000 ? item.name / 1000 + self.options.locale.km : item.name + self.options.locale.m;
      },
      updateInput(input) {
        input.setAttribute('type', 'number');
        input.setAttribute('step', '100');
      },
      setValue(value) {
        return parseInt(value, 10);
      },
    });
  }

  _createSizeSelector() {
    const self = this;
    const getValue = function (values) {
      return values.join('-');
    };
    return new PdfSheetSizeInput({
      name: 'size',
      label: self.options.locale.Size,
      unit: self.options.locale.mm,
      separator: '-',
      value: self.options.size,
      selectArray: self.options.pageFormats.map((x) => ({ name: x.name, value: getValue(x.size) })),
      getHtml(item) {
        return item.name;
      },
      getValue(values) {
        getValue(values);
      },
      setValue(value) {
        return value.split('-').map((x) => parseInt(x, 10));
      },
    });
  }

  _redrawRectangle() {
    this._cleanRectangle();
    this._drawRectangle();
  }

  _drawRectangle() {
    this._rec = L.rectangle(this.calculateRectangleEdges(), this.options.rectangleStyle);
    this._rec.addTo(this.map);
    this._rec.enableEdit();
    this._rec.on('dragend', this._onDragEnd, this);
    this._rec.on('editable:vertex:dragend', this._onEditing, this);
    this.map.fire('pdf:change', { rectangle: this._rec });
  }

  _onDragEnd() {
    this._center = this._rec.getCenter();
    this.map.fire('pdf:change', { rectangle: this._rec });
  }

  _onEditing() {
    const bounds = this._rec.getBounds();
    const topRight = L.Projection.SphericalMercator.project(bounds.getNorthEast());
    const bottomLeft = L.Projection.SphericalMercator.project(bounds.getSouthWest());
    let xy = ['x', 'y'].map((d) => {
      let delta = topRight[d] - bottomLeft[d];
      delta = (delta / this.options.scale) * 10;
      return parseInt(delta, 10);
    });
    if (this.options.orientation !== 'album') {
      xy.reverse();
    }
    this._center = this._rec.getCenter();

    const size = this._size;
    let outOfLimit = false;
    if (this.options.sizeLimit) {
      xy = xy.map((x, i) => {
        const limit = this.options.sizeLimit[i];
        if (x > limit) {
          x = limit;
          outOfLimit = true;
        }
        return x;
      });
    }

    this.options.sizeX = xy[0];
    this.options.sizeY = xy[1];
    if (size) {
      const value = `${xy[0]}-${xy[1]}`;
      size._setInput(value);
      size.redraw(value);
    } else if (outOfLimit) {
      this._redrawRectangle();
    }
    this.map.fire('pdf:change', { rectangle: this._rec });
  }

  _calculateRectangleEdges({ edgeSize, scale, center }) {
    if (typeof edgeSize === 'string') {
      edgeSize = this.options.pageFormats.find((x) => x.name === edgeSize).size;
    }
    edgeSize = edgeSize.slice();
    if (this.options.orientation !== 'album') {
      edgeSize = edgeSize.reverse();
    }
    const edgeHalfSize = edgeSize.map((x) => (x * scale) / 20);

    const point = L.Projection.SphericalMercator.project(center);
    return [
      { x: point.x - edgeHalfSize[0], y: point.y - edgeHalfSize[1] },
      { x: point.x + edgeHalfSize[0], y: point.y + edgeHalfSize[1] },
    ];
  }
}

export default PdfSheetPlugin;
